import { Link } from "react-router-dom";
import { ChevronDown, Settings, Users, LogOut } from "lucide-react";

interface UserProfileDropdownProps {
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
    roleName?: string | null;
    role?: string;
  } | null;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onLogout: () => void;
}

export function UserProfileDropdown({
  user,
  isOpen,
  onToggle,
  onClose,
  onLogout,
}: UserProfileDropdownProps) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-label="Open profile menu"
        aria-expanded={isOpen}
        className={`flex h-11 items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${isOpen ? "bg-teal-50" : "hover:bg-gray-50"}`}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-sm font-semibold text-white shadow-sm">
          {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="hidden max-w-[140px] text-left xl:block">
          <p className="truncate text-xs font-semibold leading-none text-slate-900">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="mt-1 truncate text-[10px] font-medium capitalize text-gray-500">
            {user?.roleName || user?.role || "Head Pharmacist"}
          </p>
        </div>
        <ChevronDown
          className={`hidden h-3.5 w-3.5 text-gray-400 transition-transform sm:block ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={onClose} />
          <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[80] animate-fade-in origin-top-right">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center font-bold">
                {user?.firstName?.[0]}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {user?.email}
                </p>
              </div>
            </div>

            <div className="py-2">
              <Link
                to="/profile"
                className="flex items-center px-5 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors"
                onClick={onClose}
              >
                <Users className="w-4 h-4 mr-3" />
                Your Profile
              </Link>
              <Link
                to="/settings"
                className="flex items-center px-5 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors"
                onClick={onClose}
              >
                <Settings className="w-4 h-4 mr-3" />
                Account Settings
              </Link>
            </div>

            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={onLogout}
                className="w-full text-left px-5 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors flex items-center font-medium"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
